// Settings: who you are on Lichess, how strict the analysis is, your level,
// and how the board looks.

import { useEffect, useState, type ReactNode } from "react";
import { api, errorText, isSignedOut, postJson, readJson } from "../app/api.ts";
import { bookInfo, type BookInfo } from "../app/book.ts";
import { BANDS, bandLabel, fmtCount, fmtDate } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { applyTheme, prefs, savePrefs, type Prefs } from "../app/prefs.ts";
import { navigate } from "../app/router.ts";
import { session } from "../app/session.ts";
import { toast } from "../app/toast.ts";
import { signOut } from "../app/kept.ts";
import { ScreenHead, Seg } from "../app/ui.tsx";

const BOARDS = [
  { id: "default", label: "Brown", light: "#ecdab9", dark: "#c5a076" },
  { id: "green", label: "Green", light: "#e0ddcc", dark: "#4c946a" },
  { id: "blue", label: "Blue", light: "#d8ecfb", dark: "#86afcf" },
  { id: "chess-club", label: "Club", light: "#e6d3b1", dark: "#af6b3f" },
  { id: "chessboard-js", label: "Classic", light: "#f0d9b5", dark: "#b58863" },
];

function Row({ title, sub, children, stack }: { title: ReactNode; sub?: ReactNode; children?: ReactNode; stack?: boolean }) {
  return (
    <div className={"row" + (stack ? " stack" : "")}>
      <div className="row-main">
        <div className="row-title">{title}</div>
        {sub ? <div className="row-sub">{sub}</div> : null}
      </div>
      {children ? <div className="row-ctl">{children}</div> : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

/** A preference, kept in the screen's state so the control redraws. */
function usePref<K extends keyof Prefs>(key: K, after?: () => void): [Prefs[K], (v: Prefs[K]) => void] {
  const [value, setValue] = useState(prefs[key]);
  return [
    value,
    (v) => {
      prefs[key] = v;
      savePrefs();
      setValue(v);
      after?.();
    },
  ];
}

const credit = (href: string, text: string) => (
  <a href={href} target="_blank" rel="noopener">
    {text}
  </a>
);

export function SettingsScreen() {
  const [strictness, setStrictness] = usePref("strictness");
  const [band, setBand] = usePref("band");
  const [repTree, setRepTree] = usePref("repTree");
  const [board, setBoard] = usePref("board");
  const [pieces, setPieces] = usePref("pieces");
  const [theme, setTheme] = usePref("theme", applyTheme);
  const [book, setBook] = useState<BookInfo | null>(null);

  useEffect(() => {
    bookInfo().then(setBook, () => {});
  }, []);

  const info = book?.info;
  const bookStatus = !book
    ? "Reading the book…"
    : !book.available || !info
      ? "Not installed on this server: opening names and repertoires work, statistics and courses do not."
      : [
          Number(info.built_at) ? `Built ${fmtDate(Number(info.built_at))}` : "",
          `${fmtCount(info.positions)} positions`,
          `${fmtCount(info.lichess_games)} Lichess games`,
          `${fmtCount(info.masters_games)} master games`,
          `${fmtCount(info.evaluated)} evaluated`,
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <div className="screen">
      <ScreenHead title="Settings" />
      <div className="settings">
        <Section title="Account">
          {session.authRequired ? (
            <Row title={session.email} sub="Signed in with Google.">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void signOut()}
              >
                Sign out
              </button>
            </Row>
          ) : (
            <Row title="Sign-in is off" sub="This server answers to anyone; that is how local development runs." />
          )}
        </Section>
        {/* The server itself, for whoever runs it. The sidebar carries this
            on a wide screen; a phone has no room for a seventh tab. */}
        {session.admin ? (
          <Section title="Server">
            <Row title="Admin" sub="Everyone on this server, what they use it for, and what it is holding.">
              <button type="button" className="btn btn-sm" onClick={() => navigate("/admin")}>
                <Icon name="shield" />
                Open
              </button>
            </Row>
          </Section>
        ) : null}
        <Section title="Lichess">
          <LichessRow />
        </Section>
        <Section title="Analysis">
          <Row
            title="Strictness"
            sub="How sure a position has to be before it counts. Strict keeps only tactics whose answer is unmistakable; loose widens both nets."
          >
            <Seg
              options={[
                { value: "strict", label: "Strict" },
                { value: "normal", label: "Normal" },
                { value: "loose", label: "Loose" },
              ]}
              value={strictness}
              onChange={setStrictness}
            />
          </Row>
        </Section>
        <Section title="Openings">
          <Row title="Your level" sub="Which games the book counts when it says what is played. Automatic reads it off your own games.">
            <select className="field" style={{ width: "auto" }} aria-label="Your level" value={band} onChange={(e) => setBand(e.target.value)}>
              <option value="">{book?.rating ? `Automatic (${book.rating})` : book?.band ? `Automatic (${bandLabel(book.band)})` : "Automatic"}</option>
              {BANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </Row>
          <Row
            title="Tree of moves"
            sub="Every move of a repertoire, listed beside its board. For strong players: otherwise a line is walked from the board, a move at a time."
          >
            <Seg
              options={[
                { value: "off", label: "Off" },
                { value: "on", label: "On" },
              ]}
              value={repTree ? "on" : "off"}
              onChange={(v) => setRepTree(v === "on")}
            />
          </Row>
          <Row title="Opening book" sub={bookStatus} />
          <div className="credits">
            Opening names from {credit("https://github.com/lichess-org/chess-openings", "lichess-org/chess-openings")} (CC0). Games from the{" "}
            {credit("https://database.lichess.org", "Lichess open database")} (CC0) and from {credit("https://lumbrasgigabase.com", "Lumbra's Gigabase")},
            over-the-board games with both players rated 2400+ (CC BY-NC-SA 4.0). Evaluations from the{" "}
            {credit("https://database.lichess.org/#evals", "Lichess evaluation database")} (CC0), and Reckless where it has none.
          </div>
        </Section>
        <Section title="Board">
          <Row title="Colours" stack>
            <div className="board-options">
              {BOARDS.map((b) => (
                <button key={b.id} type="button" className="board-option" aria-pressed={board === b.id} onClick={() => setBoard(b.id)}>
                  <svg viewBox="0 0 64 64" aria-hidden="true">
                    {Array.from({ length: 16 }, (_, i) => {
                      const r = Math.floor(i / 4);
                      const c = i % 4;
                      return <rect key={i} x={c * 16} y={r * 16} width="16" height="16" fill={(r + c) % 2 ? b.dark : b.light} />;
                    })}
                  </svg>
                  {b.label}
                </button>
              ))}
            </div>
          </Row>
          <Row title="Pieces">
            <Seg
              options={[
                { value: "standard", label: "Standard" },
                { value: "staunty", label: "Staunty" },
              ]}
              value={pieces}
              onChange={setPieces}
            />
          </Row>
        </Section>
        <Section title="Appearance">
          <Row title="Theme" sub="Boards follow the theme too.">
            <Seg<Prefs["theme"]>
              options={[
                { value: "system", label: "System" },
                { value: "dark", label: "Dark" },
                { value: "light", label: "Light" },
              ]}
              value={theme}
              onChange={setTheme}
            />
          </Row>
        </Section>
        <Section title="About">
          <Row title="Engine" sub={session.engine || "—"} />
          <Row title="Detection version" sub={String(session.analysisVersion || "—")} />
          <div className="credits">
            Tactic detection follows {credit("https://github.com/ornicar/lichess-puzzler", "lichess-puzzler")}; analysis by{" "}
            {credit("https://github.com/codedeliveryservice/Reckless", "Reckless")}; board by {credit("https://github.com/shaack/cm-chessboard", "cm-chessboard")}, its
            puzzle logic from {credit("https://github.com/NikZak/chess-widget", "chess-widget")}. Evaluations are shown from White's side, as on any board.
          </div>
        </Section>
      </div>
    </div>
  );
}

interface Profile {
  lichess_username?: string;
  last_game_at?: number;
  backfill_done?: boolean;
}

function describe(p: Profile, history?: { analysed?: number; oldest_game_at?: number }): string {
  if (!p.lichess_username) return "Not watching anyone yet.";
  const bits = [`Watching ${p.lichess_username}.`];
  if (p.last_game_at && p.last_game_at > 0) bits.push(`Newest game seen: ${fmtDate(Math.floor(p.last_game_at / 1000))}.`);
  if (history?.analysed) bits.push(`${fmtCount(history.analysed)} games read${history.oldest_game_at ? `, back to ${fmtDate(history.oldest_game_at)}` : ""}.`);
  if (!p.backfill_done) bits.push("Still working back through your history.");
  return bits.join(" ");
}

function LichessRow() {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await readJson(await api("/api/profile"));
        setName(data.profile.lichess_username || "");
        setStatus(describe(data.profile, data.history));
      } catch (err) {
        if (!isSignedOut(err)) setStatus(errorText(err));
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      const res = await postJson("/api/profile", { lichess_username: name });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.error || "Could not save.");
      setName(data.profile.lichess_username || "");
      setStatus(describe(data.profile, data.history));
      toast(data.profile.lichess_username ? "Saved. New games will be analysed on their own." : "Saved. Not watching anyone.");
    } catch (err) {
      if (!isSignedOut(err)) toast(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row
      stack
      title="Your Lichess player"
      sub="Set this and your games are analysed on their own, within a few minutes of finishing. Slower time controls go first. It works back through the last three months, and further — up to three years — until it has a few hundred of your games."
    >
      <div className="inline-form">
        <input
          className="field"
          type="text"
          placeholder="Lichess username"
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void save()}
        />
        <button type="button" className="btn" disabled={saving} onClick={() => void save()}>
          Save
        </button>
      </div>
      {status ? (
        <div className="row-sub" style={{ marginTop: "8px" }}>
          {status}
        </div>
      ) : null}
    </Row>
  );
}
